import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { getLocalTemplatePreviewSrc } from "./templateCatalog";

export const TEMPLATE_HOVER_PREVIEW_WIDTH = 296;
export const TEMPLATE_HOVER_PREVIEW_HEIGHT = 224;
export const TEMPLATE_HOVER_PREVIEW_GAP = 12;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export function getTemplateHoverPreviewPosition(
  anchorRect,
  viewportWidth,
  viewportHeight,
) {
  const safeRight = Math.max(
    TEMPLATE_HOVER_PREVIEW_GAP,
    viewportWidth -
      TEMPLATE_HOVER_PREVIEW_WIDTH -
      TEMPLATE_HOVER_PREVIEW_GAP,
  );
  const left = clamp(
    anchorRect.left +
      anchorRect.width / 2 -
      TEMPLATE_HOVER_PREVIEW_WIDTH / 2,
    TEMPLATE_HOVER_PREVIEW_GAP,
    safeRight,
  );
  const below = anchorRect.bottom + TEMPLATE_HOVER_PREVIEW_GAP;
  const fitsBelow =
    below + TEMPLATE_HOVER_PREVIEW_HEIGHT <=
    viewportHeight - TEMPLATE_HOVER_PREVIEW_GAP;
  const top = fitsBelow
    ? below
    : Math.max(
        TEMPLATE_HOVER_PREVIEW_GAP,
        anchorRect.top -
          TEMPLATE_HOVER_PREVIEW_HEIGHT -
          TEMPLATE_HOVER_PREVIEW_GAP,
      );

  return { left, top };
}

export default function TemplateHoverPreview({ template, anchorRect }) {
  const [imageStatus, setImageStatus] = useState("loading");
  const previewSrc = getLocalTemplatePreviewSrc(template);

  useEffect(() => {
    setImageStatus("loading");
  }, [previewSrc]);

  if (!template || !anchorRect || !previewSrc || typeof document === "undefined") {
    return null;
  }

  const title = template.label || "Template";
  const badge = (template.type || template.badge || "SCROLL").toString();
  const position = getTemplateHoverPreviewPosition(
    anchorRect,
    window.innerWidth,
    window.innerHeight,
  );

  return createPortal(
    <div
      className="tplHoverPreview"
      role="tooltip"
      aria-label={`${title} template preview`}
      style={{ left: position.left, top: position.top }}
    >
      <div className="tplHoverPreview__stage">
        {imageStatus === "loading" && (
          <div className="tplHoverPreview__loading" aria-hidden="true" />
        )}
        {imageStatus !== "error" ? (
          <img
            data-testid="template-hover-preview-image"
            className={`tplHoverPreview__image ${
              imageStatus === "loaded" ? "is-loaded" : ""
            }`}
            src={previewSrc}
            alt=""
            loading="lazy"
            decoding="async"
            onLoad={() => setImageStatus("loaded")}
            onError={() => setImageStatus("error")}
          />
        ) : (
          <span className="tplHoverPreview__fallback">Preview unavailable</span>
        )}
      </div>
      <div className="tplHoverPreview__meta">
        <span className="tplHoverPreview__title">{title}</span>
        <span className="tplHoverPreview__badge">{badge}</span>
      </div>
    </div>,
    document.body,
  );
}
